import { HttpErrorResponse } from '@angular/common/http';
import type { WorkflowFormValidationError } from '@workflows/types';
import type {
  WorkflowQueueTaskError,
  WorkflowSyncClassification,
} from './workflow-offline.types';

const RETRYABLE_STATUSES = [0, 408, 425, 429];
const RETRY_DELAYS_MS = [30_000, 60_000, 120_000, 240_000, 480_000, 900_000];

type WorkflowActionResponse = {
  isOk: boolean;
  failureReason?: string;
  context?: unknown;
};

/**
 * Задержка перед повтором: 30с → 1м → 2м → 4м → 8м → не более 15м.
 */
export function getWorkflowRetryDelayMs(attempts: number): number {
  const index = Math.min(Math.max(attempts - 1, 0), RETRY_DELAYS_MS.length - 1);

  return RETRY_DELAYS_MS[index];
}

/**
 * Классифицировать ответ сервера (в т.ч. 400 validationErrors, который HTTP-слой отдаёт как значение).
 */
export function classifyWorkflowResponse(
  response: WorkflowActionResponse,
): WorkflowSyncClassification {
  if (response.isOk) {
    return { kind: 'success' };
  }

  if (response.failureReason === 'validationErrors') {
    return {
      kind: 'validation-failed',
      error: createError('validation-failed', 400, response),
    };
  }

  return {
    kind: 'terminal',
    error: createError('terminal', 400, response),
  };
}

/**
 * Классифицировать исключение HTTP-запроса операции регламента.
 */
export function classifyWorkflowError(
  error: unknown,
): WorkflowSyncClassification {
  if (!(error instanceof HttpErrorResponse)) {
    return {
      kind: 'retryable',
      error: {
        kind: 'retryable',
        status: null,
        message: error instanceof Error ? error.message : String(error),
        at: new Date().toISOString(),
      },
    };
  }

  const body = isResponseBody(error.error) ? error.error : undefined;
  const failureReason = body?.failureReason;

  if (body && failureReason === 'validationErrors') {
    return {
      kind: 'validation-failed',
      error: createError('validation-failed', error.status, body),
    };
  }

  const isRetryable =
    RETRYABLE_STATUSES.includes(error.status) ||
    error.status >= 500 ||
    // 401: сессия истекла — задача отправится после повторного входа владельца
    error.status === 401 ||
    failureReason === 'operationInProgress';

  const kind = isRetryable ? 'retryable' : 'terminal';

  return {
    kind,
    error: {
      kind,
      status: error.status,
      failureReason,
      message: getErrorMessage(error, body),
      at: new Date().toISOString(),
    },
  };
}

function createError(
  kind: WorkflowQueueTaskError['kind'],
  status: number,
  response: WorkflowActionResponse,
): WorkflowQueueTaskError {
  const context = response.context;
  const validationErrors =
    typeof context === 'object' && context !== null
      ? (context as { validationErrors?: unknown }).validationErrors
      : undefined;

  return {
    kind,
    status,
    failureReason: response.failureReason,
    message: response.failureReason ?? 'Workflow operation failed',
    validationErrors: Array.isArray(validationErrors)
      ? (validationErrors as WorkflowFormValidationError[])
      : undefined,
    at: new Date().toISOString(),
  };
}

function isResponseBody(value: unknown): value is WorkflowActionResponse {
  return typeof value === 'object' && value !== null && 'isOk' in value;
}

function getErrorMessage(
  error: HttpErrorResponse,
  body: WorkflowActionResponse | undefined,
): string {
  const message =
    typeof error.error === 'object' && error.error !== null
      ? (error.error as { message?: unknown }).message
      : undefined;

  if (typeof message === 'string' && message) {
    return message;
  }

  return body?.failureReason ?? error.message;
}
